import { useCallback, useEffect } from "react";
import { makeVar, ReactiveVar, useReactiveVar } from "@apollo/client";
import _intersectionBy from "lodash/intersectionBy";
import { Users } from "graphql/generated/graphql";
import { auth } from "../../config/firebase/firebase";

export interface LoggedUserLocalFieldsType {
  lastUpdate?: number;
}

const loggedUserInitialValue: LoggedUserLocalFieldsType & Partial<Users> = {
  lastUpdate: Date.now(),
};

export type LoggedUserType = LoggedUserLocalFieldsType & Users;

// Function to verify if stored user matches current Firebase user
const isValidStoredUser = (storedUser: any): boolean => {
  try {
    const currentUser = auth.currentUser;

    // If no current user, or stored user has different Firebase UID, data is invalid
    if (!currentUser || !storedUser || storedUser.id !== currentUser.uid) {
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error validating stored user:", error);
    return false;
  }
};

// Function to get the initial state from localStorage or use the default value
const getInitialLoggedUserValue = (): LoggedUserType => {
  try {
    const storedValue = localStorage.getItem("loggedUser");
    if (storedValue) {
      const savedUser = JSON.parse(storedValue);

      return savedUser;
    }
  } catch (error) {
    console.error("Error parsing loggedUser from localStorage:", error);
  }
  return loggedUserInitialValue as LoggedUserType;
};

// Initialize the reactiveVar with data from localStorage or the initial value
export const loggedUserVar: ReactiveVar<LoggedUserType> =
  makeVar(getInitialLoggedUserValue());

// Function to update both reactiveVar and localStorage
const updateLoggedUserStorage = (newValue: LoggedUserType) => {
  try {
    // Always update the timestamp and add Firebase UID for validation
    const updatedValue = {
      ...newValue,
      lastUpdate: Date.now(),
    };

    localStorage.setItem("loggedUser", JSON.stringify(updatedValue));
    loggedUserVar(updatedValue);
  } catch (error) {
    console.error("Error storing loggedUser in localStorage:", error);
  }
};

// Function to clear the user when logging out
export const clearLoggedUser = () => {
  localStorage.removeItem("loggedUser");
  loggedUserVar(loggedUserInitialValue as LoggedUserType);
};

export const useLoggedUser = () => {
  const loggedUserValue = useReactiveVar(loggedUserVar);
  // Intelligent merge function that updates the user with new data
  const updateLoggedUser = useCallback(
    (data) => {
      let updatedUser: LoggedUserType = {
        ...loggedUserValue,
        ...data,
      };

      if (auth?.currentUser?.uid && updatedUser.id) {
        if (!isValidStoredUser(updatedUser)) {
          console.log("User validation failed, clearing data");
          clearLoggedUser();
        }
      }

      updateLoggedUserStorage(updatedUser);
    },
    [loggedUserValue, auth?.currentUser?.uid]
  );

  // Listen for changes in localStorage from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "loggedUser" && event.newValue) {
        try {
          const newData = JSON.parse(event.newValue);

          if (loggedUserValue) {
            // Only update if there are real changes and the data is valid
            if (
              JSON.stringify(loggedUserValue) !== event.newValue
              // && isValidStoredUser(newData)
            ) {
              // If our current version is newer, we don't update
              if (
                !newData.lastUpdate ||
                (loggedUserValue.lastUpdate &&
                  newData.lastUpdate > loggedUserValue.lastUpdate)
              ) {
                loggedUserVar(newData);
              }
            }
          }
        } catch (error) {
          console.error("Error parsing storage event data:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [loggedUserValue]);

  return {
    loggedUserValue,
    updateLoggedUser,
    isUserAnon: !loggedUserValue || !loggedUserValue.id
  };
};
